import { appConfig } from '../config/appConfig';
import { apiClient } from './apiClient';
import type { AiChatSourceParticle } from '../utils/aiChatSourceParticles';

export type UserAiChatSelectedSegment = {
  id?: string;
  particleId?: string;
  text: string;
  label?: string;
};

export type UserAiChatThread = {
  id: string;
  userId: string;
  title: string | null;
  entrypoint: string;
  segmentKey: string;
  contextByteSize: number;
  context: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export type UserAiChatMessage = {
  id: string;
  threadId: string;
  role: 'user' | 'assistant';
  content: string;
  reasoning?: string | null;
  createdAt: string;
};

export type AiChatModelOption = {
  key: string;
  family: string;
  version: string;
  label: string;
  modality: 'text' | 'multimodal' | 'image';
  mode: 'chat' | 'reasoner';
  icon: 'deepseek' | 'qwen' | 'wanx' | 'text' | 'vision';
  brandIconUrl?: string | null;
  supportsThinking: boolean;
  selectable: boolean;
};

async function authHeaders(): Promise<Record<string, string>> {
  const token = await apiClient.getAccessToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  if (token) {
    headers[appConfig.accessHeaderName] = `Bearer ${token}`;
  }
  return headers;
}

async function readJson(response: Response): Promise<any> {
  return response.json().catch(() => null);
}

function throwIfFailed(response: Response, payload: any, fallback: string): void {
  if (response.ok) return;
  const message = payload && typeof payload === 'object'
    ? String(payload.message || payload.error || fallback)
    : fallback;
  const error = new Error(message) as Error & { code?: string; status?: number };
  error.code = payload?.error;
  error.status = response.status;
  throw error;
}

function requireData<T>(payload: any, fallback: string): T {
  if (!payload || typeof payload !== 'object' || payload.data == null) {
    throw new Error(fallback);
  }
  return payload.data as T;
}

export async function listAiChatModels(): Promise<{
  models: AiChatModelOption[];
  defaultModelKey: string;
}> {
  const response = await fetch(`${appConfig.apiBaseUrl}/api/ai/chats/models`, {
    headers: await authHeaders(),
  });
  const payload = await readJson(response);
  throwIfFailed(response, payload, '加载模型列表失败');
  const data = requireData<{ models?: AiChatModelOption[]; defaultModelKey?: string }>(
    payload,
    '模型列表响应无效',
  );
  return {
    models: Array.isArray(data.models) ? data.models : [],
    defaultModelKey: data.defaultModelKey || 'deepseek:v4-flash',
  };
}

export async function getOrCreateAiChatThread(input: {
  entrypoint: string;
  surface: string;
  ttqaId?: string;
  primaryParticleId?: string;
  particleIds?: string[];
  selectedSegments: UserAiChatSelectedSegment[];
  sourceParticles?: AiChatSourceParticle[];
  deepLink?: Record<string, unknown>;
}): Promise<{
  thread: UserAiChatThread;
  messages: UserAiChatMessage[];
  created: boolean;
  contextByteLimit: number;
}> {
  const response = await fetch(`${appConfig.apiBaseUrl}/api/ai/chats`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(input),
  });
  const payload = await readJson(response);
  throwIfFailed(response, payload, '创建对话失败');
  const data = requireData<{
    thread: UserAiChatThread;
    messages?: UserAiChatMessage[];
    created: boolean;
    contextByteLimit: number;
  }>(payload, '创建对话响应无效');
  return {
    ...data,
    messages: Array.isArray(data.messages) ? data.messages : [],
  };
}

export async function listAiChatThreads(query?: {
  particleId?: string;
  ttqaId?: string;
  limit?: number;
}): Promise<UserAiChatThread[]> {
  const params = new URLSearchParams();
  if (query?.particleId) params.set('particleId', query.particleId);
  if (query?.ttqaId) params.set('ttqaId', query.ttqaId);
  if (query?.limit) params.set('limit', String(query.limit));
  const qs = params.toString();
  const response = await fetch(`${appConfig.apiBaseUrl}/api/ai/chats${qs ? `?${qs}` : ''}`, {
    headers: await authHeaders(),
  });
  const payload = await readJson(response);
  throwIfFailed(response, payload, '加载对话列表失败');
  return Array.isArray(payload?.data) ? payload.data : [];
}

export async function getAiChatThread(threadId: string): Promise<{
  thread: UserAiChatThread;
  messages: UserAiChatMessage[];
  contextByteLimit: number;
}> {
  const response = await fetch(`${appConfig.apiBaseUrl}/api/ai/chats/${encodeURIComponent(threadId)}`, {
    headers: await authHeaders(),
  });
  const payload = await readJson(response);
  throwIfFailed(response, payload, '加载对话失败');
  const data = requireData<{
    thread: UserAiChatThread;
    messages?: UserAiChatMessage[];
    contextByteLimit: number;
  }>(payload, '加载对话响应无效');
  return {
    ...data,
    messages: Array.isArray(data.messages) ? data.messages : [],
  };
}

export async function appendAiChatMessage(input: {
  threadId: string;
  message: string;
  modelKey?: string;
  mode?: string;
  signal?: AbortSignal;
  replaceFromMessageId?: string;
}): Promise<{
  thread: UserAiChatThread;
  messages: UserAiChatMessage[];
  reply: string;
  title?: string | null;
  contextByteLimit: number;
}> {
  const response = await fetch(
    `${appConfig.apiBaseUrl}/api/ai/chats/${encodeURIComponent(input.threadId)}/messages`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({
        message: input.message,
        modelKey: input.modelKey,
        mode: input.mode,
        replaceFromMessageId: input.replaceFromMessageId,
      }),
      signal: input.signal,
    },
  );
  const payload = await readJson(response);
  throwIfFailed(response, payload, '发送失败');
  const data = requireData<{
    thread: UserAiChatThread;
    messages?: UserAiChatMessage[];
    reply: string;
    title?: string | null;
    contextByteLimit: number;
  }>(payload, '发送响应无效，请重试');
  return {
    ...data,
    messages: Array.isArray(data.messages) ? data.messages : [],
  };
}

type AiChatStreamDonePayload = {
  thread: UserAiChatThread;
  messages?: UserAiChatMessage[];
  reply: string;
  title?: string | null;
  contextByteLimit: number;
};

/** 流式发送：Pro 可边想边出；Flash 仅 content 增量 */
export async function appendAiChatMessageStream(input: {
  threadId: string;
  message: string;
  modelKey?: string;
  mode?: string;
  signal?: AbortSignal;
  replaceFromMessageId?: string;
  onReasoningDelta?: (delta: string) => void;
  onContentDelta?: (delta: string) => void;
}): Promise<{
  thread: UserAiChatThread;
  messages: UserAiChatMessage[];
  reply: string;
  title?: string | null;
  contextByteLimit: number;
}> {
  const headers = await authHeaders();
  headers.Accept = 'text/event-stream, application/json';

  const response = await fetch(
    `${appConfig.apiBaseUrl}/api/ai/chats/${encodeURIComponent(input.threadId)}/messages/stream`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        message: input.message,
        modelKey: input.modelKey,
        mode: input.mode,
        replaceFromMessageId: input.replaceFromMessageId,
      }),
      signal: input.signal,
    },
  );

  const contentType = String(response.headers.get('content-type') || '');
  if (!contentType.includes('text/event-stream')) {
    const payload = await readJson(response);
    throwIfFailed(response, payload, '发送失败');
    const data = requireData<{
      thread: UserAiChatThread;
      messages?: UserAiChatMessage[];
      reply: string;
      title?: string | null;
      contextByteLimit: number;
    }>(payload, '发送响应无效，请重试');
    return { ...data, messages: Array.isArray(data.messages) ? data.messages : [] };
  }

  if (!response.ok) {
    const payload = await readJson(response);
    throwIfFailed(response, payload, '发送失败');
    throw new Error('流式响应无效');
  }

  let donePayload: AiChatStreamDonePayload | null = null;

  const flushEvent = (rawEvent: string) => {
    const lines = rawEvent.split('\n');
    let eventName = 'message';
    const dataLines: string[] = [];
    for (const line of lines) {
      if (line.startsWith('event:')) eventName = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }
    if (!dataLines.length) return;
    let parsed: any = null;
    try {
      parsed = JSON.parse(dataLines.join('\n'));
    } catch {
      return;
    }
    if (eventName === 'reasoning' && typeof parsed?.delta === 'string') {
      input.onReasoningDelta?.(parsed.delta);
      return;
    }
    if (eventName === 'content' && typeof parsed?.delta === 'string') {
      input.onContentDelta?.(parsed.delta);
      return;
    }
    if (eventName === 'error') {
      const error = new Error(String(parsed?.message || parsed?.error || '生成失败')) as Error & { code?: string };
      error.code = parsed?.error;
      throw error;
    }
    if (eventName === 'done' && parsed?.data) {
      donePayload = parsed.data;
    }
  };

  const consumeSseBuffer = async (readChunk: () => Promise<{ done: boolean; text: string }>) => {
    let buffer = '';
    while (true) {
      const { done, text } = await readChunk();
      if (text) {
        buffer += text;
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          if (part.trim()) flushEvent(part);
        }
      }
      if (done) break;
    }
    if (buffer.trim()) flushEvent(buffer);
  };

  const body = response.body;
  if (body && typeof body.getReader === 'function') {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    await consumeSseBuffer(async () => {
      const { done, value } = await reader.read();
      return {
        done,
        text: decoder.decode(value || new Uint8Array(), { stream: !done }),
      };
    });
  } else {
    // 部分运行时无 ReadableStream：整包 text 再解析（无增量，但仍可完成）
    const text = await response.text();
    await consumeSseBuffer(async () => ({ done: true, text }));
  }

  if (!donePayload) {
    throw new Error('流式响应未完成');
  }
  const finished: AiChatStreamDonePayload = donePayload;
  return {
    ...finished,
    messages: Array.isArray(finished.messages) ? finished.messages : [],
  };
}

export async function listAiChatAnchors(particleId: string): Promise<Array<{
  threadId: string;
  segmentKey: string;
  texts: string[];
}>> {
  const params = new URLSearchParams({ particleId });
  const response = await fetch(`${appConfig.apiBaseUrl}/api/ai/chats/anchors?${params}`, {
    headers: await authHeaders(),
  });
  const payload = await readJson(response);
  throwIfFailed(response, payload, '加载对话锚点失败');
  if (Array.isArray(payload?.data?.anchors)) return payload.data.anchors;
  const keys = Array.isArray(payload?.data?.segmentKeys) ? payload.data.segmentKeys : [];
  return keys.map((segmentKey: string) => ({ threadId: '', segmentKey, texts: [] }));
}

export async function listAiChatAnchorsBatch(particleIds: string[]): Promise<Record<string, Array<{
  threadId: string;
  segmentKey: string;
  texts: string[];
}>>> {
  const ids = [...new Set(particleIds.map((id) => String(id || '').trim()).filter(Boolean))];
  const empty: Record<string, Array<{ threadId: string; segmentKey: string; texts: string[] }>> = {};
  for (const id of ids) empty[id] = [];
  if (!ids.length) return empty;

  try {
    const response = await fetch(`${appConfig.apiBaseUrl}/api/ai/chats/anchors/batch`, {
      method: 'POST',
      headers: {
        ...(await authHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ particleIds: ids }),
    });
    const payload = await readJson(response);
    throwIfFailed(response, payload, '加载对话锚点失败');
    const byParticleId = payload?.data?.byParticleId;
    if (byParticleId && typeof byParticleId === 'object') {
      for (const id of ids) {
        empty[id] = Array.isArray(byParticleId[id]) ? byParticleId[id] : [];
      }
      return empty;
    }
  } catch {
    // Legacy AppFunctor without batch: fall back per-id.
  }

  const entries = await Promise.all(ids.map(async (particleId) => {
    try {
      return [particleId, await listAiChatAnchors(particleId)] as const;
    } catch {
      return [particleId, [] as Array<{ threadId: string; segmentKey: string; texts: string[] }>] as const;
    }
  }));
  for (const [particleId, anchors] of entries) {
    empty[particleId] = [...anchors];
  }
  return empty;
}
